import React, { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Users, Crown, Check } from 'lucide-react';
import { TecnicoAsignado } from '@/contexts/OrdenesTrabajoContext';
import { cn } from '@/lib/utils';

interface Tecnico {
  user_id: string;
  nombre: string;
  avatar_url?: string | null;
}

interface AsignarTecnicosPopoverProps {
  tecnicos: Tecnico[];
  initialAsignados: TecnicoAsignado[];
  onConfirm: (asignados: TecnicoAsignado[]) => Promise<void> | void;
  trigger?: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  buttonLabel?: string;
}

const initials = (n: string) =>
  n.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();

export const AsignarTecnicosPopover: React.FC<AsignarTecnicosPopoverProps> = ({
  tecnicos,
  initialAsignados,
  onConfirm,
  trigger,
  align = 'end',
  buttonLabel = 'Asignar técnicos',
}) => {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<TecnicoAsignado[]>(initialAsignados);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setSel(initialAsignados);
    // Only re-sync when the popover transitions to open, to avoid infinite
    // re-render loops when the parent passes a new array reference each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isSel = (id: string) => sel.some(s => s.tecnico_id === id);
  const rolOf = (id: string) => sel.find(s => s.tecnico_id === id)?.rol;

  const toggle = (t: Tecnico) => {
    if (isSel(t.user_id)) {
      const remaining = sel.filter(s => s.tecnico_id !== t.user_id);
      if (rolOf(t.user_id) === 'principal' && remaining.length > 0) {
        remaining[0] = { ...remaining[0], rol: 'principal' };
      }
      setSel(remaining);
    } else {
      const rol: 'principal' | 'apoyo' = sel.length === 0 ? 'principal' : 'apoyo';
      setSel([...sel, { tecnico_id: t.user_id, rol, nombre: t.nombre, avatar_url: t.avatar_url }]);
    }
  };

  const setPrincipal = (id: string) => {
    setSel(sel.map(s => ({ ...s, rol: (s.tecnico_id === id ? 'principal' : 'apoyo') as 'principal' | 'apoyo' })));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onConfirm(sel);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <Button size="sm" variant="outline" className="gap-1" onClick={(e) => e.stopPropagation()}>
            <Users className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{buttonLabel}</span>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 z-[9999]"
        align={align}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 border-b">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm">Asignar técnicos</span>
            {sel.length > 0 && (
              <Badge variant="secondary" className="ml-auto">{sel.length}</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Marca uno o varios. La corona indica el técnico principal.
          </p>
        </div>
        <ScrollArea className="max-h-64">
          <div className="p-2 space-y-1">
            {tecnicos.length === 0 && (
              <p className="text-sm text-muted-foreground p-3 text-center">No hay técnicos disponibles</p>
            )}
            {tecnicos.map(t => {
              const selected = isSel(t.user_id);
              const rol = rolOf(t.user_id);
              return (
                <div
                  key={t.user_id}
                  className={cn(
                    'flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors',
                    selected ? 'bg-primary/10' : 'hover:bg-muted'
                  )}
                  onClick={() => toggle(t)}
                >
                  <Checkbox checked={selected} className="pointer-events-none" />
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={t.avatar_url || undefined} />
                    <AvatarFallback className="text-[10px]">{initials(t.nombre)}</AvatarFallback>
                  </Avatar>
                  <span className="flex-1 min-w-0 truncate text-sm">{t.nombre}</span>
                  {selected && (
                    rol === 'principal' ? (
                      <Badge variant="default" className="gap-1 text-[10px]">
                        <Crown className="h-3 w-3" />
                        Principal
                      </Badge>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-6 text-[11px] px-2"
                        onClick={(e) => { e.stopPropagation(); setPrincipal(t.user_id); }}
                      >
                        Hacer principal
                      </Button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
        <div className="p-3 border-t flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1">
            <Check className="h-3.5 w-3.5" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
